import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../App";
import { apiDelete, apiGet, apiPost, getApiErrorMessage } from "../lib/gentleflowApi";
import { MessageCircle, X, Send, Loader2, Sparkles, User, Trash2, Mic, MicOff, Globe2, ShieldCheck } from "lucide-react";
import AmbientMusicToggle from "./AmbientMusicToggle";
import DropFlowTransition from "./DropFlowTransition";
import DashboardCompanion from "./DashboardCompanion";
import { useLocation } from "react-router-dom";

const GentleflowChat = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [listening, setListening] = useState(false);
  const [voiceSupported, setVoiceSupported] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [showAuthTransition, setShowAuthTransition] = useState(false);
  const previousAuth = useRef(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);

  const WELCOME_MSG = {
    role: "assistant",
    content: "Namaste! I'm the Gentleflow Self-Consultant — an empathetic wellness guide for cycle tracking, symptoms, comfort and personal care. I know the Gentleflow app and can help you think through everyday wellness questions."
  };

  useEffect(() => {
    if (!previousAuth.current && isAuthenticated) {
      setShowAuthTransition(true);
      const timer = setTimeout(() => setShowAuthTransition(false), 1800);
      previousAuth.current = true;
      return () => clearTimeout(timer);
    }
    previousAuth.current = isAuthenticated;
  }, [isAuthenticated]);

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;
    setVoiceSupported(true);
    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = navigator.language || "en-IN";
    recognition.onresult = (event) => {
      let transcript = "";
      for (let i = event.resultIndex; i < event.results.length; i += 1) transcript += event.results[i][0].transcript;
      setInput(transcript);
    };
    recognition.onend = () => setListening(false);
    recognition.onerror = () => setListening(false);
    recognitionRef.current = recognition;
    return () => { try { recognition.stop(); } catch {} };
  }, []);

  const toggleVoice = () => {
    if (!recognitionRef.current || loading) return;
    if (listening) {
      try { recognitionRef.current.stop(); } catch {}
      setListening(false);
      return;
    }
    try {
      recognitionRef.current.lang = navigator.language || "en-IN";
      recognitionRef.current.start();
      setListening(true);
    } catch {
      setListening(false);
    }
  };

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);
  useEffect(() => { if (isOpen) inputRef.current?.focus(); }, [isOpen]);

  useEffect(() => {
    if (!isOpen || historyLoaded || !isAuthenticated) return;
    let cancelled = false;
    const loadHistory = async () => {
      try {
        const res = await apiGet("/chat/history");
        if (cancelled) return;
        const msgs = Array.isArray(res.data?.messages) ? res.data.messages : [];
        setMessages(msgs.length ? msgs.map(m => ({ role: m.role, content: m.content })) : [WELCOME_MSG]);
        if (res.data?.session_id) setSessionId(res.data.session_id);
      } catch (error) {
        if (!cancelled) setMessages([WELCOME_MSG]);
      } finally {
        if (!cancelled) setHistoryLoaded(true);
      }
    };
    loadHistory();
    return () => { cancelled = true; };
  }, [isOpen, historyLoaded, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      setHistoryLoaded(false);
      setSessionId(null);
    }
    if (isOpen && !isAuthenticated && messages.length === 0) setMessages([WELCOME_MSG]);
  }, [isOpen, isAuthenticated, messages.length]);

  useEffect(() => {
    const consumePrefill = () => {
      const prompt = localStorage.getItem("gentleflow_ai_prefill");
      if (!prompt) return;
      localStorage.removeItem("gentleflow_ai_prefill");
      setIsOpen(true);
      setInput(prompt);
    };
    window.addEventListener("gentleflow-ai-prefill", consumePrefill);
    return () => window.removeEventListener("gentleflow-ai-prefill", consumePrefill);
  }, []);

  const sendMessage = async (text = input) => {
    const userMessage = text.trim();
    if (!userMessage || loading) return;
    if (!isAuthenticated) {
      setInput("");
      setMessages(prev => [...prev, { role: "user", content: userMessage }, { role: "assistant", content: "Please sign in to use the Gentleflow Self-Consultant and unlock personalized cycle guidance." }]);
      return;
    }

    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);
    try {
      const response = await apiPost("/chat", {
        message: userMessage,
        session_id: sessionId || null,
        include_personal_context: true
      });
      setSessionId(response.data?.session_id || null);
      setMessages(prev => [...prev, { role: "assistant", content: response.data?.response || "I couldn't generate a response right now. Please try again." }]);
    } catch (error) {
      const detail = getApiErrorMessage(error);
      setMessages(prev => [...prev, { role: "assistant", content: detail }]);
    } finally {
      setLoading(false);
    }
  };

  const clearChat = async () => {
    try { await apiDelete("/chat/history"); } catch {}
    setMessages([WELCOME_MSG]);
    setSessionId(null);
  };

  const openConsultantWithPrompt = (prompt) => {
    localStorage.setItem("gentleflow_ai_prefill", prompt);
    window.dispatchEvent(new Event("gentleflow-ai-prefill"));
  };

  const suggestedQuestions = ["What can Gentleflow do?", "Explain my current cycle phase", "What can I do for cramps?", "How should I track symptoms?"];

  return (
    <>
      <AmbientMusicToggle />
      {showAuthTransition && <DropFlowTransition message="Welcome to Gentleflow" />}
      {isAuthenticated && location.pathname === "/dashboard" && <DashboardCompanion onAsk={openConsultantWithPrompt} />}

      <button onClick={() => setIsOpen(true)} className={`fixed bottom-20 right-6 z-50 group flex items-center gap-2 rounded-full bg-gradient-to-r from-[#4A1942] to-[#C084A0] px-5 py-3 text-white shadow-[0_14px_40px_rgba(74,25,66,0.2)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(74,25,66,0.28)] ${isOpen ? "hidden" : "flex"}`} data-testid="chat-open-btn">
        <span className="relative flex h-6 w-6 items-center justify-center rounded-full bg-white/15"><Sparkles className="h-4 w-4" /><span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-green-300 shadow-[0_0_0_4px_rgba(134,239,172,0.15)]" /></span>
        <span className="font-medium">Self-Consultant</span>
      </button>

      {isOpen && (
        <div className="fixed bottom-20 right-6 z-50 flex w-[410px] max-w-[calc(100vw-32px)] flex-col overflow-hidden rounded-[28px] border border-[#E8E3DB] bg-white shadow-[0_30px_80px_rgba(74,25,66,0.22)] animate-in slide-in-from-bottom-4 fade-in duration-300" style={{ height: "min(640px, calc(100vh - 140px))" }} data-testid="chat-window">
          <div className="relative overflow-hidden bg-gradient-to-br from-[#4A1942] via-[#8d5a78] to-[#C084A0] p-5">
            <div className="pointer-events-none absolute -right-12 -top-16 h-40 w-40 rounded-full bg-white/10 blur-2xl" />
            <div className="relative flex items-center justify-between">
              <div className="flex items-center gap-3"><div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-white/20 bg-white/15 shadow-lg backdrop-blur-sm"><Sparkles className="h-5 w-5 text-white" /></div><div><h3 className="font-semibold text-white">Gentleflow Self-Consultant</h3><p className="mt-0.5 flex items-center gap-1.5 text-xs text-white/75"><span className="h-1.5 w-1.5 rounded-full bg-green-300" /> Empathetic wellness guide</p></div></div>
              <div className="flex items-center gap-1"><button onClick={clearChat} className="rounded-full p-2 transition-colors hover:bg-white/10" title="Clear chat"><Trash2 className="h-4 w-4 text-white/80" /></button><button onClick={() => setIsOpen(false)} className="rounded-full p-2 transition-colors hover:bg-white/10" data-testid="chat-close-btn"><X className="h-5 w-5 text-white" /></button></div>
            </div>
            <div className="relative mt-4 flex items-center gap-2 text-[11px] text-white/75"><Globe2 className="h-3.5 w-3.5" /> Gentleflow knowledge + cycle context + general AI guidance</div>
          </div>

          <div className="flex-1 space-y-4 overflow-y-auto bg-[#faf8f5] p-4">
            {messages.map((msg, index) => <div key={`msg-${index}-${msg.role}`} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"} animate-in fade-in slide-in-from-bottom-2 duration-200`}><div className={`flex max-w-[88%] items-end gap-2 ${msg.role === "user" ? "flex-row-reverse" : ""}`}><div className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full ${msg.role === "user" ? "bg-[#FFBE98]" : "bg-[#C084A0]"}`}>{msg.role === "user" ? <User className="h-4 w-4 text-[#4A2937]" /> : <Sparkles className="h-4 w-4 text-white" />}</div><div className={`rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${msg.role === "user" ? "rounded-br-md bg-[#C084A0] text-white" : "rounded-bl-md border border-[#E8E3DB] bg-white text-[#4A4A4A] shadow-sm"}`}>{msg.content}</div></div></div>)}
            {loading && <div className="flex items-center gap-2"><div className="flex h-7 w-7 items-center justify-center rounded-full bg-[#C084A0]"><Sparkles className="h-4 w-4 text-white" /></div><div className="rounded-2xl border border-[#E8E3DB] bg-white px-4 py-3"><div className="flex gap-1"><span className="h-2 w-2 animate-bounce rounded-full bg-[#C084A0]" /><span className="h-2 w-2 animate-bounce rounded-full bg-[#C084A0] [animation-delay:150ms]" /><span className="h-2 w-2 animate-bounce rounded-full bg-[#C084A0] [animation-delay:300ms]" /></div></div></div>}
            <div ref={messagesEndRef} />
          </div>

          {messages.length <= 1 && !loading && <div className="border-t border-[#E8E3DB] bg-[#faf8f5] px-4 py-2.5"><p className="mb-2 text-[11px] text-[#8A7A85]">Try asking your Self-Consultant</p><div className="flex flex-wrap gap-2">{suggestedQuestions.map(q => <button key={q} onClick={() => sendMessage(q)} className="rounded-full border border-[#E8E3DB] bg-white px-3 py-1.5 text-xs text-[#5B4956] transition-all hover:border-[#C084A0] hover:bg-[#C084A0] hover:text-white">{q}</button>)}</div></div>}

          <div className="border-t border-[#E8E3DB] bg-white p-3"><div className="flex items-center gap-2 rounded-2xl border border-[#E8E3DB] bg-[#faf8f5] p-1.5 focus-within:border-[#C084A0] focus-within:ring-2 focus-within:ring-[#C084A0]/10">{voiceSupported && <button type="button" onClick={toggleVoice} disabled={loading} className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl transition-all ${listening ? "bg-[#4A1942] text-white shadow-lg" : "text-[#8A7A85] hover:bg-[#C084A0]/10 hover:text-[#C084A0]"}`} title={listening ? "Stop listening" : "Speak to Self-Consultant"} data-testid="chat-voice-btn">{listening ? <MicOff className="h-5 w-5 animate-pulse" /> : <Mic className="h-5 w-5" />}</button>}<input ref={inputRef} type="text" value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }} placeholder={listening ? "Listening…" : "Tell me what you're experiencing…"} className="min-w-0 flex-1 bg-transparent px-2 py-2 text-sm text-[#4A4A4A] outline-none placeholder:text-[#A1A1A1]" disabled={loading} data-testid="chat-input" /><button onClick={() => sendMessage()} disabled={!input.trim() || loading} className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl transition-all ${input.trim() && !loading ? "bg-[#C084A0] text-white hover:bg-[#A96B87] hover:scale-105" : "bg-[#E8E3DB] text-[#A1A1A1]"}`} data-testid="chat-send-btn">{loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}</button></div><div className="mt-2 flex items-center justify-center gap-1 text-[10px] text-[#A1A1A1]"><ShieldCheck className="h-3 w-3" /> Wellness guidance only · not a diagnosis or substitute for professional care</div></div>
        </div>
      )}
    </>
  );
};

export default GentleflowChat;
